import { CartAddEvent, ThemeEvents } from '@theme/events';

/**
 * PASHION — Modules panier :
 *  - <pc-ensemble>         : « Complète l'ensemble » (ajoute la pièce manquante)
 *  - <pc-size-swap>        : changement de taille d'une ligne sans la supprimer
 *  - <pc-countdown>        : compte à rebours fin de précommande
 *  - <pc-sticky-checkout>  : barre « Commander » collante, masquée quand le vrai bouton est visible
 */
const root = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
const JSON_HEADERS = { 'Content-Type': 'application/json', Accept: 'application/json' };

function sectionIdOf(el) {
  const holder = el.closest('cart-items-component[data-section-id]');
  return (holder && holder.dataset.sectionId) || el.dataset.sectionId || null;
}

function refreshCart(el, source, data) {
  const sectionId = sectionIdOf(el);
  if (!sectionId || !data || !data.sections) {
    window.location.reload();
    return;
  }
  document.dispatchEvent(
    new CartAddEvent({}, sectionId, { source, itemCount: data.item_count, sections: data.sections })
  );
}

/* ---------- Complète l'ensemble ---------- */
class PcEnsemble extends HTMLElement {
  connectedCallback() {
    this._onClick = this._onClick.bind(this);
    this._onChange = this._onChange.bind(this);
    this.addEventListener('click', this._onClick);
    this.addEventListener('change', this._onChange);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this._onClick);
    this.removeEventListener('change', this._onChange);
  }

  _onChange() {
    this.classList.remove('pc-ens--need-size');
  }

  async _onClick(event) {
    const btn = event.target.closest('[data-pc-add]');
    if (!btn) return;
    event.preventDefault();
    if (btn.getAttribute('aria-busy') === 'true') return;

    const checked = this.querySelector('input[type="radio"]:checked');
    if (!checked) {
      this.classList.add('pc-ens--need-size');
      return;
    }

    btn.setAttribute('aria-busy', 'true');
    const sectionId = sectionIdOf(this);

    try {
      const res = await fetch(`${root()}cart/add.js`, {
        method: 'POST',
        headers: JSON_HEADERS,
        body: JSON.stringify({
          items: [{ id: Number(checked.value), quantity: 1 }],
          sections: sectionId ? [sectionId] : [],
          sections_url: window.location.pathname,
        }),
      });
      if (!res.ok) throw new Error('add');
      const data = await res.json();
      this.classList.add('pc-ens--added');
      refreshCart(this, 'pc-ensemble', data);
    } catch (e) {
      btn.setAttribute('aria-busy', 'false');
      this.classList.add('pc-ens--error');
    }
  }
}

/* ---------- Changement de taille ---------- */
class PcSizeSwap extends HTMLElement {
  connectedCallback() {
    this._onClick = this._onClick.bind(this);
    this.addEventListener('click', this._onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this._onClick);
  }

  _mark(id) {
    this.querySelectorAll('.pc-swap__chip').forEach((c) => {
      const on = Number(c.dataset.id) === Number(id);
      c.classList.toggle('is-on', on);
      c.setAttribute('aria-checked', on ? 'true' : 'false');
    });
  }

  async _onClick(event) {
    const chip = event.target.closest('.pc-swap__chip');
    if (!chip || chip.disabled || chip.classList.contains('is-on') || this.hasAttribute('busy')) return;
    event.preventDefault();

    const msg = this.querySelector('.pc-swap__msg');
    if (msg) msg.textContent = '';
    const newId = Number(chip.dataset.id);
    const qty = Number(this.dataset.qty) || 1;
    const key = this.dataset.key;
    const sectionId = sectionIdOf(this);

    this.setAttribute('busy', '');
    this._mark(newId);

    try {
      const add = await fetch(`${root()}cart/add.js`, {
        method: 'POST',
        headers: JSON_HEADERS,
        body: JSON.stringify({ items: [{ id: newId, quantity: qty }] }),
      });
      if (!add.ok) {
        const d = await add.json().catch(() => ({}));
        throw new Error(d.description || 'Cette taille n\u2019est plus disponible.');
      }
      const res = await fetch(`${root()}cart/change.js`, {
        method: 'POST',
        headers: JSON_HEADERS,
        body: JSON.stringify({
          id: key,
          quantity: 0,
          sections: sectionId ? [sectionId] : [],
          sections_url: window.location.pathname,
        }),
      });
      if (!res.ok) throw new Error('Oups, réessaie.');
      const data = await res.json();
      refreshCart(this, 'pc-size-swap', data);
    } catch (e) {
      this.removeAttribute('busy');
      this._mark(this.dataset.current);
      if (msg) msg.textContent = (e && e.message) || 'Oups, réessaie.';
    }
  }
}

/* ---------- Compte à rebours ---------- */
class PcCountdown extends HTMLElement {
  connectedCallback() {
    this.end = Date.parse(this.dataset.end);
    if (isNaN(this.end)) return;
    this._tick = this._tick.bind(this);
    this._tick();
    this._t = setInterval(this._tick, 1000);
  }

  disconnectedCallback() {
    clearInterval(this._t);
  }

  _tick() {
    let s = Math.floor((this.end - Date.now()) / 1000);
    if (s <= 0) {
      this.textContent = this.dataset.ended || 'Terminé';
      clearInterval(this._t);
      return;
    }
    const d = Math.floor(s / 86400);
    s %= 86400;
    const h = Math.floor(s / 3600);
    s %= 3600;
    const m = Math.floor(s / 60);
    s %= 60;
    const p = (n) => String(n).padStart(2, '0');
    this.textContent = (this.dataset.prefix || '') + (d > 0 ? d + 'j ' : '') + p(h) + ':' + p(m) + ':' + p(s);
    this.classList.toggle('pc-cd--urgent', d < 2);
  }
}

/* ---------- Barre Commander collante ---------- */
class PcStickyCheckout extends HTMLElement {
  connectedCallback() {
    this._rebind = this._rebind.bind(this);
    this._rebind();
    document.addEventListener(ThemeEvents.cartUpdate, this._onUpdate = () => setTimeout(this._rebind, 120));
  }

  disconnectedCallback() {
    if (this._io) this._io.disconnect();
    document.removeEventListener(ThemeEvents.cartUpdate, this._onUpdate);
  }

  _rebind() {
    if (this._io) this._io.disconnect();
    const scope = this.closest('cart-items-component') || document;
    const target = scope.querySelector('.cart__ctas');
    const scroller = scope.querySelector('[data-pc-scroll]');
    if (!target || !('IntersectionObserver' in window)) return;
    this._io = new IntersectionObserver(
      (entries) => this.classList.toggle('is-hidden', entries[0].isIntersecting),
      { root: scroller || null, threshold: 0.2 }
    );
    this._io.observe(target);
  }
}

if (!customElements.get('pc-ensemble')) customElements.define('pc-ensemble', PcEnsemble);
if (!customElements.get('pc-size-swap')) customElements.define('pc-size-swap', PcSizeSwap);
if (!customElements.get('pc-countdown')) customElements.define('pc-countdown', PcCountdown);
if (!customElements.get('pc-sticky-checkout')) customElements.define('pc-sticky-checkout', PcStickyCheckout);
